// Clarity layer for the mentor<->founder Google Meet flow.
//
// The mechanism is deliberately invisible: a Favcy "platform bot" is the Meet
// organizer for every mentor/founder call, so Google auto-saves the recording
// to the bot's Drive, a watcher cron ingests it, and AI agents summarize it —
// nobody installs a note-taker or hits "record". That invisibility is exactly
// why people don't understand it. These components surface (a) an always-on
// recording notice + DPDP disclosure and (b) a collapsible "how it works"
// panel, on both the mentor and founder engagement workspaces.
//
// IMPORTANT: the canonical consent record is written SERVER-SIDE at schedule
// time (call_consent_log + the verbatim RECORDING_DISCLOSURE_V1 in the calendar
// event, via services/mentor-call-scheduler.ts). The checkbox some callers
// render in their schedule form is a visible acknowledgement for the human
// scheduling the call — it is NOT the legal record and must not be treated as
// one.
//
// We deliberately DO NOT reproduce the verbatim legal disclosure here. Copying
// it onto the client would create two strings that silently drift apart. The
// notice below is a plain-language explainer that points users at the
// authoritative sources of the notice (their calendar invite + Google Meet's
// own recording banner), so there is a single source of legal truth.
import { useState } from "react";
import { Info, CalendarPlus, Video, PlayCircle, Sparkles, ChevronDown, ChevronRight } from "lucide-react";

export type CallGuideScope = "mentor" | "founder";

// Per-scope token map. Founder side is Rule-3 guarded (opacity floor /60, brand
// orange only); mentor side uses the --mentor-* theme tokens so it remaps with
// the portal's light/dark toggle.
const TONE: Record<CallGuideScope, { box: string; fg: string; muted: string; hover: string; divider: string }> = {
  mentor: {
    box: "bg-black/20 border-[var(--mentor-border)]",
    fg: "text-[var(--mentor-fg)]",
    muted: "text-[var(--mentor-fg-muted)]",
    hover: "hover:text-[var(--mentor-fg)]",
    divider: "border-[var(--mentor-border)]",
  },
  founder: {
    box: "bg-white/[0.04] border-white/10",
    fg: "text-white/85",
    muted: "text-white/60",
    hover: "hover:text-white",
    divider: "border-white/10",
  },
};

// Always-visible inline notice so neither party is surprised that the call is
// recorded, and so they know where the recording + AI summary will appear.
export function RecordingNotice({ scope }: { scope: CallGuideScope }) {
  const t = TONE[scope];
  return (
    <div className={`flex items-start gap-2 rounded-md border ${t.box} px-3 py-2.5 mb-3`} data-testid="recording-notice">
      <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#FF5C00]" />
      <div className={`text-[11px] leading-relaxed ${t.muted}`}>
        <span className={`font-semibold ${t.fg}`}>Hosted &amp; auto-recorded by Favcy.</span>{" "}
        This call is hosted by Favcy and recorded for your engagement archive. Google Meet shows its own recording banner during the call, and the formal recording notice is in your calendar invite. The recording and an AI-written summary appear here automatically within ~10 minutes of the call ending — nothing to upload.
      </div>
    </div>
  );
}

// Collapsible "how it works" panel. Closed by default so it doesn't crowd the
// page, but one click reveals the full four-step flow.
export function HowMentorCallsWork({ scope }: { scope: CallGuideScope }) {
  const [open, setOpen] = useState(false);
  const t = TONE[scope];
  const steps: Array<{ icon: React.ReactNode; title: string; body: string }> = [
    {
      icon: <CalendarPlus className="w-3.5 h-3.5 text-[#FF5C00]" />,
      title: "1. Pick a slot",
      body: "Either of you can schedule. The other side automatically gets a Google Calendar invite with the Meet link — no emails are shared between you.",
    },
    {
      icon: <Video className="w-3.5 h-3.5 text-[#FF5C00]" />,
      title: "2. Join the call",
      body: "Use the Join Meet button here or the link in your calendar invite. The call is hosted by Favcy and recorded — Google shows its native \u201crecording\u201d banner so everyone knows.",
    },
    {
      icon: <PlayCircle className="w-3.5 h-3.5 text-[#FF5C00]" />,
      title: "3. Recording lands here",
      body: "When the call ends, the recording is saved automatically and shows up in this Calls section within about 10 minutes. No one has to download or re-upload anything.",
    },
    {
      icon: <Sparkles className="w-3.5 h-3.5 text-[#FF5C00]" />,
      title: "4. AI summary",
      body: "Favcy's agents transcribe the recording and pull out the key facts, commitments and follow-ups, so progress is captured without manual note-taking.",
    },
  ];
  return (
    <div className={`rounded-md border ${t.box} mb-3`} data-testid="how-calls-work">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-[11px] font-semibold ${t.fg}`}
        data-testid="button-toggle-how-calls-work"
        aria-expanded={open}
      >
        <span className="inline-flex items-center gap-1.5">
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          How mentor calls work
        </span>
        <span className={`text-[10px] font-normal ${t.muted}`}>{open ? "Hide" : "Recorded & summarized — tap to learn how"}</span>
      </button>
      {open && (
        <div className={`border-t ${t.divider} px-3 py-3 space-y-3`}>
          {steps.map((s) => (
            <div key={s.title} className="flex items-start gap-2.5">
              <div className="mt-0.5 flex-shrink-0">{s.icon}</div>
              <div>
                <div className={`text-xs font-semibold ${t.fg}`}>{s.title}</div>
                <div className={`text-[11px] leading-relaxed ${t.muted} mt-0.5`}>{s.body}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
